import React from 'react';
import { Image, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { BadgeCheck, CalendarDays, Check, Mic, ShieldCheck, Star, Zap } from '../components/icons';
import { FondNuit } from '../components/FondNuit';
import { useGabarit } from '../hooks/useGabarit';
import { ScreenHeader } from '../components/ScreenHeader';
import { GradientButton } from '../components/GradientButton';
import { GradientOutlineButton } from '../components/GradientOutlineButton';
import { useTarifsCoaching } from '../hooks/useTarifsCoaching';
import { libellePrix, tarifPret } from '../services/tarifsCoaching';
import {
  COACHING_FORMULES,
  JULIEN,
  type CoachingFormule,
  type CoachingFormuleId,
} from '../config/coaching';
import { colors, formes, gradients, layout, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import { encrer } from '../theme/featureAccents';
import { t } from '../i18n';

export interface CoachingScreenProps {
  onBack?: () => void;
  /** Fourni quand la réservation est ouverte → CTA actifs (→ choix du créneau, puis paiement). */
  onReserve?: (formule: CoachingFormuleId) => void;
}

// Texte violet / corail posé sur un tint clair : l'encre assombrit la teinte jusqu'au contraste AA.
const VIOLET_ENCRE = encrer(colors.violetLogo, 5.6);
const CORAIL_ENCRE = encrer(colors.corail, 5.6);

const ETAPES = [
  { cle: 'coaching.step1', fond: colors.blueSoft, encre: colors.electricInk },
  { cle: 'coaching.step2', fond: colors.limeSoft, encre: VIOLET_ENCRE },
  { cle: 'coaching.step3', fond: colors.corailSoft, encre: CORAIL_ENCRE },
] as const;

export function CoachingScreen({ onBack, onReserve }: CoachingScreenProps) {
  const { large } = useGabarit();
  const { etat, reessayer } = useTarifsCoaching();
  const reservable = !!onReserve;
  const [anciennete, communaute, activites] = JULIEN.stats;

  const renderFormule = (f: CoachingFormule) => {
    const BadgeIcon = f.highlight ? Star : Zap;
    const encre = f.highlight ? VIOLET_ENCRE : colors.electricInk;
    const prix = libellePrix(etat, f.id);
    const actif = reservable && tarifPret(etat, f.id);
    return (
      <>
        <View style={[styles.badge, f.highlight ? styles.badgeVip : styles.badgeStd]}>
          <BadgeIcon size={12} color={encre} strokeWidth={2.5} fill={encre} />
          <Text style={[styles.badgeText, { color: encre }]}>{f.badge}</Text>
        </View>

        <View style={styles.titreLigne}>
          <View style={styles.titreBloc}>
            <Text style={styles.formuleTitle}>{f.title}</Text>
            <Text style={styles.formuleSubtitle}>{f.subtitle}</Text>
          </View>
          {/* AUCUN REPLI. Tant que le tarif n'est pas lu, la place reste vide — jamais un montant
              du dépôt, qui serait un prix que personne n'a confronté au serveur. */}
          {prix ? (
            <Text testID={`coaching-prix-${f.id}`} style={[styles.price, { color: encre }]}>
              {prix}
            </Text>
          ) : null}
        </View>

        <View style={styles.points}>
          {f.points.map((p, index) => (
            <View key={index} style={styles.pointRow}>
              <Check size={16} color={colors.electricDeep} strokeWidth={2.6} />
              <Text style={styles.pointText}>{p}</Text>
            </View>
          ))}
        </View>

        {f.highlight ? (
          <GradientButton
            variante="cta"
            hauteur="lg"
            label={reservable ? t('coaching.pickSlot') : t('coaching.soon')}
            icon={<CalendarDays size={18} color={colors.white} strokeWidth={2.2} />}
            disabled={!actif}
            onPress={() => onReserve?.(f.id)}
            testID={`coaching-reserve-${f.id}`}
          />
        ) : (
          <GradientOutlineButton
            label={reservable ? t('coaching.pickSlot') : t('coaching.soon')}
            icon={<CalendarDays size={18} color={colors.electric} strokeWidth={2} />}
            disabled={!actif}
            onPress={() => onReserve?.(f.id)}
            testID={`coaching-reserve-${f.id}`}
          />
        )}

        {/* L'ÉTAT INDISPONIBLE N'EST PAS UN CUL-DE-SAC : une seule nouvelle tentative a déjà eu
            lieu automatiquement, il reste le geste. Sans ça, un accroc réseau fermerait le
            coaching jusqu'à ce que la personne quitte l'écran et y revienne — sans rien à l'écran
            pour le lui dire. */}
        {etat.statut === 'indisponible' ? (
          <View style={styles.tarifIndispo} testID={`coaching-tarif-indispo-${f.id}`}>
            <Text style={styles.tarifIndispoText}>{t('coaching.tarifsIndisponibles')}</Text>
            <Pressable
              onPress={reessayer}
              testID="coaching-tarif-reessayer"
              accessibilityRole="button"
              hitSlop={12}
              style={styles.reessayerCible}
            >
              <Text style={styles.tarifReessayer}>{t('coaching.reessayer')}</Text>
            </Pressable>
          </View>
        ) : null}

        <View style={styles.reassureRow}>
          <ShieldCheck size={14} color={colors.success} strokeWidth={2.4} />
          <Text style={styles.reassureText}>{t('coaching.slotBeforePayment')}</Text>
        </View>
      </>
    );
  };

  const sHero = (
    <>
        {/* Carte du coach : identité, puis trois chiffres clés */}
        <FondNuit testID="coaching-hero" style={styles.hero}>
          <View testID="coaching-julien" style={styles.heroContenu}>
            <View style={styles.expertRow}>
              {/* Anneau dégradé fixe : la maquette le fait tourner, ce qui demandera l'option
                  « réduire les animations » qu'elle exige. */}
              <LinearGradient
                colors={[colors.cyan, colors.electric, colors.violetLogo, colors.corail]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.anneau}
              >
                <Image
                  source={require('../../assets/coach-julien.png')}
                  style={styles.photo}
                  resizeMode="cover"
                  testID="coaching-julien-photo"
                  accessible
                  accessibilityLabel={JULIEN.name}
                />
              </LinearGradient>
              <View style={styles.expertText}>
                <View style={styles.nameRow}>
                  <Text style={styles.expertName}>{JULIEN.name}</Text>
                  <BadgeCheck size={18} color={colors.cyan} strokeWidth={2.5} />
                </View>
                <Text style={styles.roleLead}>{JULIEN.roleLead}</Text>
                <Text style={styles.roleBrand}>{JULIEN.roleBrand}</Text>
              </View>
            </View>

            <View style={styles.chips}>
              <View style={[styles.chip, styles.chipEtroit]} testID="coaching-stat-0">
                <Text style={styles.chipValeur}>{anciennete.value}</Text>
                <Text style={styles.chipLibelle}>{anciennete.label}</Text>
              </View>
              <View style={[styles.chip, styles.chipLarge]} testID="coaching-stat-1">
                <Text style={styles.chipValeur}>{communaute.value}</Text>
                <Text style={styles.chipLibelle}>{communaute.label}</Text>
              </View>
            </View>
            <View style={styles.bandeau} testID="coaching-stat-2">
              <Mic size={16} color={colors.white} strokeWidth={2} />
              <Text style={styles.bandeauTexte}>{`${activites.value} ${activites.label}`}</Text>
            </View>
          </View>
        </FondNuit>

            </>
  );
  const sEtapes = (
    <>
{/* Déroulé. Il ne dit pas à quel moment le créneau est confirmé : cette promesse vit sous les
            boutons, au même endroit que le parcours de paiement qu'elle décrit. */}
        <View style={styles.etapes} testID="coaching-etapes">
          <Text style={styles.sectionTitle}>{t('coaching.howTitle')}</Text>
          <View style={styles.etapesLigne}>
            {ETAPES.map((e, index) => (
              <View
                key={e.cle}
                testID={`coaching-etape-${index + 1}`}
                accessible
                accessibilityLabel={`${index + 1}, ${t(e.cle)}`}
                style={styles.etape}
              >
                <View style={[styles.etapeNumero, { backgroundColor: e.fond }]}>
                  <Text style={[styles.etapeNumeroTexte, { color: e.encre }]}>{index + 1}</Text>
                </View>
                <Text style={styles.etapeTexte}>{t(e.cle)}</Text>
              </View>
            ))}
          </View>
        </View>

            </>
  );
  const sTitreFormules = (
    <>
<Text style={styles.sectionTitle}>{t('coaching.pickFormule')}</Text>

            </>
  );
  const sFormules = (
    <>
{COACHING_FORMULES.map((f) =>
          f.highlight ? (
            <LinearGradient
              key={f.id}
              testID={`coaching-formule-${f.id}`}
              colors={gradients.cta}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={[styles.vipBorder, large && styles.carteFormuleL]}
            >
              <View style={styles.vipInner}>{renderFormule(f)}</View>
            </LinearGradient>
          ) : (
            <View key={f.id} testID={`coaching-formule-${f.id}`} style={[styles.formuleCard, large && styles.carteFormuleL]}>
              {renderFormule(f)}
            </View>
          ),
        )}
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={onBack} title={t('coaching.title')} variante="carte" testID="coaching-back" />

              {large ? (
          <ScrollView contentContainerStyle={styles.pageL} showsVerticalScrollIndicator={false}>
            <View style={styles.hautL}>
              <View style={styles.hautGaucheL}>{sHero}</View>
              <View style={styles.hautDroiteL}>{sEtapes}</View>
            </View>
            {sTitreFormules}
            <View style={styles.formulesL}>{sFormules}</View>
          </ScrollView>
        ) : (
          <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
            {sHero}
            {sEtapes}
            {sTitreFormules}
            {sFormules}
          </ScrollView>
        )}
    </SafeAreaView>
  );
}

const PHOTO = 80;
const ANNEAU = 86;
const BLANC_VOILE = 'rgba(255, 255, 255, 0.85)';

const styles = StyleSheet.create({
  colonnesL: {
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
  },
  colGaucheL: { flex: 1, minWidth: 0, gap: spacing.lg },
  colDroiteL: { flex: 1, minWidth: 0, gap: spacing.md },
  piedL: { marginTop: spacing.sm },
  pageL: {
    flexGrow: 1,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
    gap: spacing.lg,
  },
  hautL: { flexDirection: 'row', alignItems: 'stretch', gap: spacing.xl },
  hautGaucheL: { flex: 1.1, minWidth: 0 },
  hautDroiteL: { flex: 1, minWidth: 0, justifyContent: 'center' },
  formulesL: { flexDirection: 'row', alignItems: 'stretch', gap: spacing.xl },
  carteFormuleL: { flex: 1, minWidth: 0, marginTop: 0 },
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, gap: spacing.lg, paddingBottom: spacing.xxl },

  // ─── Carte du coach ───
  hero: { borderRadius: formes.rayon.lg },
  heroContenu: { padding: spacing.xl, gap: spacing.sm },
  expertRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.lg, marginBottom: spacing.sm },
  anneau: {
    width: ANNEAU,
    height: ANNEAU,
    borderRadius: ANNEAU / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  photo: {
    width: PHOTO,
    height: PHOTO,
    borderRadius: PHOTO / 2,
    borderWidth: 3,
    borderColor: colors.night,
    backgroundColor: colors.grey100,
  },
  expertText: { flex: 1, gap: 3 },
  nameRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs + 2 },
  expertName: { flexShrink: 1, fontSize: typography.h2.size, fontWeight: '800', color: colors.white },
  roleLead: { fontSize: 14, color: colors.blancAtenue },
  roleBrand: { fontSize: typography.small.size, fontWeight: '700', color: colors.pecheNuit },

  chips: { flexDirection: 'row', gap: spacing.sm },
  chip: {
    borderRadius: formes.rayon.md,
    backgroundColor: colors.surfaceNuitForte,
    borderWidth: 1,
    borderColor: colors.pisteSombre,
    paddingVertical: spacing.sm + 2,
    paddingHorizontal: spacing.sm,
    alignItems: 'center',
  },
  chipEtroit: { flex: 1 },
  chipLarge: { flex: 1.7 },
  chipValeur: { fontSize: 16, lineHeight: 22, fontWeight: '800', color: colors.white, textAlign: 'center' },
  chipLibelle: { fontSize: 12, lineHeight: 16, color: BLANC_VOILE, textAlign: 'center' },
  bandeau: {
    minHeight: formes.hauteur.sm,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.surfaceNuitForte,
    borderWidth: 1,
    borderColor: colors.pisteSombre,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  bandeauTexte: { flexShrink: 1, fontSize: 13, lineHeight: 18, color: BLANC_VOILE, textAlign: 'center' },

  sectionTitle: { fontSize: 17, fontWeight: '800', color: colors.night },

  // ─── Déroulé en trois étapes ───
  etapes: { gap: spacing.sm + 2 },
  etapesLigne: { flexDirection: 'row', gap: spacing.sm },
  etape: {
    flex: 1,
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: 18,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  etapeNumero: { width: 28, height: 28, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  etapeNumeroTexte: { fontSize: 14, fontWeight: '800' },
  etapeTexte: { fontSize: 13, lineHeight: 17, fontWeight: '700', color: colors.night },

  // ─── Formules ───
  formuleCard: {
    padding: spacing.lg,
    borderRadius: formes.rayon.lg,
    backgroundColor: colors.white,
    gap: spacing.md,
    ...shadow.card,
  },
  vipBorder: { borderRadius: formes.rayon.lg, padding: 2, ...shadow.card },
  vipInner: {
    padding: spacing.lg,
    borderRadius: formes.rayon.lg - 2,
    backgroundColor: colors.white,
    gap: spacing.md,
  },

  badge: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs + 1,
    borderRadius: 13,
    paddingHorizontal: spacing.md - 2,
    paddingVertical: spacing.xs + 1,
  },
  badgeVip: { backgroundColor: colors.limeSoft },
  badgeStd: { backgroundColor: colors.blueSoft },
  badgeText: { fontSize: 12, fontWeight: '800' },

  titreLigne: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing.md },
  titreBloc: { flex: 1, gap: 2 },
  formuleTitle: { fontSize: 21, lineHeight: 27, fontWeight: '800', color: colors.night },
  formuleSubtitle: { fontSize: typography.small.size, lineHeight: 18, color: colors.grey500 },
  price: { fontSize: 26, lineHeight: 32, fontWeight: '800' },

  points: { gap: spacing.sm - 1 },
  pointRow: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  pointText: { flex: 1, fontSize: 14, lineHeight: 20, color: colors.grey700 },

  tarifIndispo: { gap: 4, alignItems: 'center' },
  tarifIndispoText: { fontSize: typography.small.size, color: colors.grey500, textAlign: 'center' },
  reessayerCible: { minHeight: formes.cible, justifyContent: 'center' },
  tarifReessayer: { fontSize: typography.small.size, fontWeight: '700', color: colors.electricInk },
  reassureRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs + 2 },
  reassureText: { flexShrink: 1, fontSize: 12.5, color: colors.grey500, textAlign: 'center' },
});
